import { scaleLinear, scaleTime } from "d3-scale"
import { line, curveLinear } from "d3-shape"
import { axisBottom, axisLeft, axisRight } from "d3-axis"
import moment from "moment"
import { find, last, first, get, chain, isNull, filter, size, pick, map, mapValues, includes } from "lodash"
import { createSelector } from "reselect"
import { xAxisTicks } from "../helpers/x-axis-ticks"

const MARGINS = { top: 15, left: 80, right: 85, bottom: 15 }

export const getData = props => props.data
const getDate = props => props.date
const getWidth = props => props.width || 150
const getStartDate = props => props.startDate
const getEndDate = props => props.endDate
const getHeight = props => props.height
const getItems = props => props.items
const getTickFormat = props => props.tickFormat
const getOnly = props => props.onlyItem
const getLeftYScaleIds = props => get(props.yScaleIds, "left")
const getRawRightYScaleIds = props => get(props.yScaleIds, "right")

const getRightYScaleIds = createSelector(
  [getItems, getLeftYScaleIds, getRawRightYScaleIds],
  (items, leftIds, rightIds) => {
    if (!rightIds) {
      return chain(items)
        .map(({ id }) => id)
        .difference(leftIds)
        .value()
    }
    return rightIds
  },
)
const getFilteredItems = createSelector(
  [getItems],
  (items) => items.filter(item => !item.disabled),
)

export const getOnlyLegendText = createSelector(
  [getOnly],
  (only) => only ? "All" : "Only",
)

const getMinX = createSelector(
  [getStartDate, getData],
  (startDate, data) => {
    if (data && data.length > 0) return first(data).date.toDate()
    return moment(startDate).toDate()
  },
)

const getMaxX = createSelector(
  [getEndDate, getData],
  (endDate, data) => {
    if (data && data.length > 0) return last(data).date.toDate()
    return moment(endDate).toDate()
  },
)

const getXTicks = createSelector(
  [getMinX, getMaxX],
  xAxisTicks,
)

const getMinMaxY = createSelector(
  [getData, getFilteredItems],
  (data, items) =>
    chain(data)
      .map(datum => pick(datum, map(items, item => item.id)))
      .reduce((res, datum) => ({
        min: mapValues(datum, (v, k) => Math.min(get(res, ["min", k], Infinity), v)),
        max: mapValues(datum, (v, k) => Math.max(get(res, ["max", k], -Infinity), v)),
      }), {})
      .value(),
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getWidth],
  (minX, maxX, width) => scaleTime()
    .domain([minX, maxX])
    .rangeRound([MARGINS.left, width - MARGINS.left]),
)

const getLeftScaleYMinMax = createSelector(
  [getMinMaxY, getLeftYScaleIds],
  calculateMinMaxScale,
)

export const getLeftScaleY = createSelector(
  [getLeftScaleYMinMax, getHeight],
  (minMaxY, height) => {
    const [min, max] = minMaxY
    return scaleLinear()
      .domain([min, max])
      .rangeRound([height - MARGINS.bottom, MARGINS.top])
  },
)

const getRightScaleYMinMax = createSelector(
  [getMinMaxY, getRightYScaleIds],
  calculateMinMaxScale,
)

export const getRightScaleY = createSelector(
  [getRightScaleYMinMax, getHeight],
  (minMaxY, height) => {
    const [min, max] = minMaxY

    return scaleLinear()
      .domain([min, max])
      .rangeRound([height - MARGINS.bottom, MARGINS.top])
  },
)

export const getLines = createSelector(
  [getScaleX, getLeftScaleY, getRightScaleY, getData, getFilteredItems, getLeftYScaleIds, getRightYScaleIds],
  (scaleX, scaleYLeft, scaleYRight, data, items, leftIds, rightIds) => {
    const lines = chain(items)
      .keyBy(item => item.id)
      .mapValues(item => {
        const points = map(data, d => ({
          date: d.date,
          [item.id]: d[item.id],
          index: d.index,
        }))
        const filtered = filter(points, (p) => !isNull(p[item.id]))
        const missing = filter(points, (p) => p[item.id] != null)
        // If filtered and missing have the same length, there is no missing data
        if (size(filtered) === size(missing)) {
          return { filtered }
        }

        // If the first point in the missing line is not index 0, than we need to add fake points
        // in order for d3 to be able to draw a line from the beginning
        let corrected = missing
        const firstDot = first(missing)
        if (firstDot.index !== 0) {
          corrected = [
            ...map([...Array(firstDot.index).keys()], (i) => ({
              date: get(data, [i, "date"]),
              [item.id]: 0,
              index: i,
            })),
            ...corrected,
          ]
        }

        return {
          filtered,
          missing: first(missing).index === 0 ? missing : corrected,
        }
      })
      .value()

    const getScale = (id) => (d) => includes(leftIds, id) ? scaleYLeft(get(d, id)) : scaleYRight(get(d, id))

    return items
      .map(({ id, color }) => {
        const obj = ({
          id,
          d: line()
            .x(d => scaleX(d.date))
            .y(d => getScale(id)(d))
            .defined(d => d[id] != null)
            .curve(curveLinear)(lines[id].filtered),
          style: { stroke: color },
          dashed: includes(rightIds, id),
        })
        // If we have missing data line points, return a line object
        if (lines[id].missing) {
          obj.missing = line()
            .x(d => scaleX(d.date))
            .y(d => getScale(id)(d))
            .curve(curveLinear)(lines[id].missing)
        }
        return obj
      })
  },
)

export const getXAxis = createSelector(
  [getHeight, getWidth, getScaleX, getXTicks],
  (height, width, scaleX, [ticks, formatter]) => ({
    fn: axisBottom()
      .scale(scaleX)
      .ticks(ticks)
      .tickSize(-height + MARGINS.top + MARGINS.bottom)
      .tickFormat(formatter),
    transform: `translate(0,${height - MARGINS.bottom})`,
    baseline: {
      x1: MARGINS.left,
      x2: width - MARGINS.left,
      y1: height - MARGINS.bottom,
      y2: height - MARGINS.bottom,
    },
  }),
)

export const geYAxisColors = createSelector(
  [getItems],
  (items) => chain(items)
    .filter(item => item.scaleColor)
    .groupBy("scaleY")
    .mapValues(items => first(items).color)
    .value(),
)

export const getLeftYAxis = createSelector(
  [getHeight, getWidth, getLeftScaleY, getTickFormat, geYAxisColors],
  (height, width, scaleY, tickFormat, colors) => ({
    fn: axisLeft()
      .scale(scaleY)
      .tickSize(-width + MARGINS.left + MARGINS.right - 20)
      .tickFormat(tickFormat),
    transform: `translate(${MARGINS.left},0)`,
    stroke: colors.left,
    labelTransform: {
      x: -(MARGINS.left - 15),
      y: height / 2,
    },
    baseline: {
      x1: MARGINS.left,
      x2: MARGINS.left,
      y1: MARGINS.top,
      y2: height - MARGINS.bottom,
      stroke: colors.left,
    },
  }),
)

export const getRightYAxis = createSelector(
  [getHeight, getWidth, getRightScaleY, getTickFormat, geYAxisColors],
  (height, width, scaleY, tickFormat, colors) => ({
    fn: axisRight()
      .scale(scaleY)
      .tickSize(4)
      .tickFormat(tickFormat),
    transform: `translate(${width - MARGINS.left - 4},0)`,
    labelTransform: {
      x: MARGINS.left - 11,
      y: height / 2,
    },
    stroke: colors.right,
    baseline: {
      x1: width - MARGINS.left,
      x2: width - MARGINS.left,
      y1: MARGINS.top,
      y2: height - MARGINS.bottom,
      stroke: colors.right,
    },
  }),
)

export const getFindByOffset = createSelector(
  [getData, getScaleX],
  (data, scaleX) => offset => {
    const lookup = scaleX.invert(offset - 18) // -2 is to give a margin of selection on the first element, -16 for the margin
    return find(data, datum => datum.date >= lookup)
  },
)

export const getHighlighter = createSelector(
  [getDate, getScaleX, getRightScaleY, getMaxX, getMinMaxY],
  (date, scaleX, scaleY, maxX, minMaxY) => {
    const { min, max } = minMaxY
    const minY = chain(min)
      .omit("total")
      .map()
      .min()
      .value()
    const maxY = chain(max)
      .omit("total")
      .map()
      .max()
      .value()
    date = moment(date)
    const maxDate = moment(maxX)

    if (!date.isSame(maxDate)) {
      const x = scaleX(date.toDate())
      return {
        x1: x,
        x2: x,
        y1: scaleY(minY) + 15, // Makes the highlighter overflow of 15px below the chart.
        y2: scaleY(maxY) - 15, // Makes the highlighter overflow of 15px above the chart.
      }
    } else {
      return null
    }
  },
)

function calculateMinMaxScale(minMaxY, ids) {
  const { min, max } = minMaxY
  const minY = chain(min)
    .pick(ids)
    .map()
    .min()
    .value()
  const maxY = chain(max)
    .pick(ids)
    .map()
    .max()
    .value()
  return [minY, maxY]
}
